import { useCallback, useState } from "react";
import { Contract } from "ethers";
import useConnectWallet from "@/store/wallet/useConnectWallet";

// ERC20 ABI - 只需要 symbol 方法
const ERC20_ABI = [
    "function symbol() view returns (string)",
    "function name() view returns (string)",
    "function decimals() view returns (uint8)"
];

/**
 * TokenInfo - 代币基本信息
 */
export interface TokenInfo {
    symbol: string;      // 代币符号，如 "USDT", "DAI"
    name: string;        // 代币名称
    decimals: number;    // 代币精度
}

/**
 * useGetTokenSymbol - 获取 ERC20 代币信息 Hook
 * 
 * 用于调用 ERC20 合约获取代币的 symbol、name 和 decimals。
 * 
 * @returns {Object} 返回获取方法、结果和加载状态
 *   - getTokenInfo: 执行获取代币信息的函数
 *   - tokenInfoCache: 已缓存的代币信息（按地址索引）
 *   - tokenInfoLod: 加载状态
 */
const useGetTokenSymbol = () => {
    // 存放数据（缓存所有查询过的代币信息）
    const [tokenInfoCache, setTokenInfoCache] = useState<{ [address: string]: TokenInfo }>({});
    // loading
    const [tokenInfoLod, setLoading] = useState(false);
    // 获取 provider
    const { provider } = useConnectWallet();

    /**
     * 获取指定代币的信息
     * @param tokenAddress ERC20 代币地址
     * @returns 返回代币信息 (symbol, name, decimals)
     */
    const getTokenInfo = useCallback(async (tokenAddress: string): Promise<TokenInfo | null> => {
        const lowerAddress = tokenAddress.toLowerCase();

        // 如果已经缓存，直接返回
        if (tokenInfoCache[lowerAddress]) {
            return tokenInfoCache[lowerAddress];
        }

        setLoading(true);
        if (provider) {
            try {
                // 创建 ERC20 合约实例
                const tokenContract = new Contract(tokenAddress, ERC20_ABI, provider);

                // 并行获取 symbol, name, decimals
                const [symbol, name, decimals] = await Promise.all([
                    tokenContract.symbol(),
                    tokenContract.name(),
                    tokenContract.decimals()
                ]);

                const info: TokenInfo = {
                    symbol,
                    name,
                    decimals: Number(decimals)
                };

                // 保存到缓存
                setTokenInfoCache(prev => ({
                    ...prev,
                    [lowerAddress]: info
                }));

                setLoading(false);
                return info;
            } catch (e) {
                console.log("useGetTokenSymbol", e);
                setLoading(false);
                return null;
            }
        }
        setLoading(false);
        return null;
    }, [provider, tokenInfoCache]);

    /**
     * 批量获取多个代币的信息
     * @param tokenAddresses ERC20 代币地址数组
     * @returns 返回代币信息映射表
     */
    const getTokenInfoBatch = useCallback(async (tokenAddresses: string[]): Promise<{ [address: string]: TokenInfo }> => {
        const result: { [address: string]: TokenInfo } = {};

        // 过滤掉已经缓存的地址
        const uncachedAddresses = tokenAddresses.filter(
            addr => !tokenInfoCache[addr.toLowerCase()]
        );

        // 先把缓存的结果加入
        tokenAddresses.forEach(addr => {
            const cached = tokenInfoCache[addr.toLowerCase()];
            if (cached) {
                result[addr.toLowerCase()] = cached;
            }
        });

        if (uncachedAddresses.length === 0) {
            return result;
        }

        setLoading(true);

        // 并行获取所有未缓存的代币信息
        const promises = uncachedAddresses.map(addr => getTokenInfo(addr));
        const infos = await Promise.all(promises);

        infos.forEach((info, index) => {
            if (info) {
                const addr = uncachedAddresses[index].toLowerCase();
                result[addr] = info;
            }
        });

        setLoading(false);
        return result;
    }, [tokenInfoCache, getTokenInfo]);

    return { getTokenInfo, getTokenInfoBatch, tokenInfoCache, tokenInfoLod };
};

export default useGetTokenSymbol;
